import { useState } from 'react'
import api from '../../api/axios'
import type { Device } from './fleet'

// Sends a real test notification of a chosen kind to whoever is signed in on
// this device, through the same payload builder the real reminders use — so
// the native buttons (Clock out, Switch job), the replace-in-tray behaviour
// and the Android channel can be checked on an actual phone. Bypasses quiet
// hours and the once-per-day reminder limits. POST
// /super-admin/devices/:id/test-push; the result lists every phone it tried.

const KINDS = [
  ['clock_out_reminder', 'Clock-out reminder', 'Buttons: Clock out, Still working'],
  ['meeting_reminder', 'Meeting reminder', 'Buttons: Switch job, Still here'],
  ['clock_in_reminder', 'Clock-in reminder', 'Button: Clock in'],
  ['break_reminder', 'Break reminder', 'Reminders channel'],
  ['overtime_alert', 'Overtime alert', 'Buttons: Clock out, Still working'],
  ['timeoff_status', 'Time off decision', 'Messages channel, opens Time off'],
  ['message', 'Office message', 'Messages channel, no buttons'],
] as const

type Kind = (typeof KINDS)[number][0]

interface DeliveryResult {
  platform: string
  status: string
  error?: string | null
}

const STATUS_TEXT: Record<string, string> = {
  sent: 'Sent',
  failed: 'Failed',
  pruned: 'Token removed',
  no_device: 'No phone',
  held: 'Held',
}

export default function DeviceTestPush({
  device,
  notify,
  onClose,
}: {
  device: Device
  notify: (msg: string, tone?: 'ok' | 'error') => void
  onClose: () => void
}) {
  const [kind, setKind] = useState<Kind>('clock_out_reminder')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const [results, setResults] = useState<DeliveryResult[] | null>(null)

  const send = async () => {
    setSending(true)
    setError('')
    setResults(null)
    try {
      const r = await api.post(`/super-admin/devices/${device.id}/test-push`, { type: kind })
      const deliveries: DeliveryResult[] = r.data?.deliveries || []
      setResults(deliveries)
      const ok = deliveries.filter((d) => d.status === 'sent').length
      notify(ok ? `Test sent to ${ok} phone${ok === 1 ? '' : 's'}` : 'Test not delivered', ok ? 'ok' : 'error')
    } catch (err) {
      setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || "Couldn't send the test")
    } finally {
      setSending(false)
    }
  }

  return (
    <section className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3" aria-label="Send a test notification">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">Test notification for {device.employee_name || 'this person'}</h3>
        <p className="text-xs text-slate-500">
          A real notification to every phone they're signed in on, marked &ldquo;Test:&rdquo;. Ask them to lock the phone, then
          long-press it (iPhone) or expand it (Android) to see the buttons.
        </p>
      </div>

      <fieldset className="space-y-1.5">
        <legend className="sr-only">Kind</legend>
        {KINDS.map(([k, label, hint]) => (
          <label key={k} className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="test-push-kind"
              value={k}
              checked={kind === k}
              onChange={() => setKind(k)}
              className="mt-0.5 text-blue-600 focus:ring-blue-500"
            />
            <span>
              <span className="font-medium text-slate-800">{label}</span>
              <span className="block text-xs text-slate-500">{hint}</span>
            </span>
          </label>
        ))}
      </fieldset>

      {error && <p className="text-xs text-red-600">{error}</p>}
      {results && (
        <ul className="text-xs space-y-1" aria-live="polite">
          {results.length === 0 && <li className="text-slate-500">No phones tried.</li>}
          {results.map((d, i) => (
            <li key={i} className="flex gap-2">
              <span className="font-medium text-slate-700 w-16">{d.platform === 'ios' ? 'iPhone' : d.platform === 'android' ? 'Android' : d.platform}</span>
              <span className={d.status === 'sent' ? 'text-emerald-700' : 'text-red-600'}>{STATUS_TEXT[d.status] || d.status}</span>
              {d.error && <span className="text-slate-500 truncate">{d.error}</span>}
            </li>
          ))}
        </ul>
      )}

      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onClose} className="px-3 py-2 text-sm rounded-lg border border-slate-200 bg-white hover:bg-slate-50">
          Close
        </button>
        <button
          type="button"
          disabled={sending}
          onClick={send}
          className="px-3 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {sending ? 'Sending…' : 'Send test'}
        </button>
      </div>
    </section>
  )
}
